import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { animateScroll } from 'react-scroll';

import {
  Button,
  Card,
  CardBody,
  Col,
  FormFeedback,
  FormGroup,
  Label,
  Row,
  TabContent,
  TabPane,
  Alert
} from 'reactstrap';
import CredsModal from '../Vendor/CredsModal';
import SummaryModal from '../Vendor/Modal';

import TabNav from '../section/TabNav';
import TabOne from '../section/TabOne';
import BZAPIService from '../../../services/BZAPIService';
import DefaultConfigTab from '../section/DefaultConfigTab';
import ProductPriority from '../section/ProductPriority';
import ExecutionStatsTab from '../section/ExecutionStatsTab';
import { useScheduling } from '../../../hooks/useScheduling';
// constants
import SETTINGS from '../../../constants/settings';
import ButtonIcon from '../../common/ButtonIcon';
import { FieldGroup } from '../../../libs/formLib';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Select from 'react-select';

const ConfigFormAwsConfig = ({
  isReadOnly,
  vendorLabel,
  bugsDateBack,
  setBugsDateBack,
  vendorId,
  vendorPriorityOptions,
  vendorStatusOptions,
  vendorResolutionOptions,
  snIntegrationCheck,
  services,
  vendors,
  priorityStats,
  setPriorityStats,
  snCompanySysIdOptions,
  snCompanySysIdErrors,
  vendorEventScopes,
  vendorResolutions,
  setVendorResolutions,
  setVendorStatuses,
  vendorStatuses,
  fields,
  handleFieldChange,
  priorityMaps,
  setPriorityMaps,
  snPriorityOptions,
  keywords,
  setKeywords,
  searchFields,
  setSearchFields,
  snCompanySysId,
  setSnCompanySysId,
  setServices,
  setVendors,
  buildVendorStat,
  setBugs,
  isConfigured,
  syncOnly,
  setSyncOnly,
  loading,
  isServiceActive,
  setIsServiceActive,
  vendorBugCategories,
  setVendorBugCategories,
  vendorBugTypeOptions,
  vendorBugTypes,
  setVendorBugTypes,
  vendorImpactedRegions,
  setVendorImpactedRegions,
  vendorImpactedRegionOptions,
  allRequired,
  setAllRequired,
  isUpdated,
  setIsUpdated,
  vendor,
  settings
}) => {
  console.log('settings', settings);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const [isValidCredentials, setIsValidCredentials] = useState(false);
  const [isTestingValidCredentials, setIsTestingValidCredentials] = useState(false);
  const [isAddingCredentials, setIsAddingCredentials] = useState(false);
  const [isUpdatingCreds, setIsUpdatingCreds] = useState(false);

  const [activeTab, setActiveTab] = useState('1');
  const [modalOpen, setModalOpen] = useState(false);
  const [validated, setValidated] = useState(null);
  const [isForceRun, setIsForceRun] = useState(false);

  const [aggregator, setAggregator] = useState(null);
  const [aggregatorOptions, setAggregatorOptions] = useState([]);

  // Use scheduling hook for state and save function
  const {
    timeUnit,
    setTimeUnit,
    timeValue,
    setTimeValue,
    schedulingMode,
    setSchedulingMode,
    schedules,
    setSchedules,
    nextRun,
    setNextRun,
    saveBugServiceFrequency
  } = useScheduling();

  // const [orgHealthError, setOrgHealthError] = useState();

  async function setBugServiceFrequency() {
    await saveBugServiceFrequency(vendorId);
  }

  async function saveAWSArn() {
    const data = {
      secret: {
        awsConfigApiRoleArn: fields.awsConfigApiRoleArn
      },
      vendorId: 'aws',
      key: 'configArn'
    };
    // cloud apps secret
    return BZAPIService.createOrUpdateAWSSecret(data);
  }

  async function testCreds() {
    setIsTestingValidCredentials(true);
    const data = {
      awsConfigApiRoleArn: fields.awsConfigApiRoleArn
    };
    const result = await BZAPIService.vendorTestCreds(vendorId, data);
    setIsTestingValidCredentials(false);
    if (result) {
      // valid creds
      if (!result.roleAssumed) return toast.error(`Unable to assume role.`);
      if (!result.configApiAccess) return toast.error(`Unable to access AWS Config API.`);

      // Set aggregator options if they exist in the response
      if (result.aggregators) {
        // Transform string array into array of objects with value and label properties for react-select
        const formattedAggregators = result.aggregators.map(aggregator => ({
          value: aggregator,
          label: aggregator
        }));
        setAggregatorOptions(formattedAggregators);
      }

      setIsValidCredentials(true);
      setIsAddingCredentials(false);
      setIsUpdatingCreds(true);
      toast.success(`AWS Health API Role ARN validated.`);
    } else {
      toast.error(`Unable to validate AWS Health API Role ARN.`);
    }
  }

  async function saveConfig(serviceSecret) {
    // save client config
    const vendorPriorities = priorityMaps.map(mapping => {
      return {
        value: mapping.vendorPriority.value,
        label: mapping.vendorPriority.value,
        snPriority: mapping.snPriority.value
      };
    });

    const data = {
      bugsDateBack: bugsDateBack,
      secretId: serviceSecret.Name,
      snCompanySysId: snCompanySysId.value ? snCompanySysId.value : fields.snCompanySysId,
      snAffectedCIQuery: fields.snAffectedCIQuery,
      vendorPriorities,
      type: SETTINGS.VENDOR,
      excludedKws: keywords,
      searchFields,
      vendorImpactedRegions,
      aggregatorName: aggregator ? aggregator.value : null
    };

    return BZAPIService.saveSetting(vendorId, syncOnly, data, isForceRun, isUpdatingCreds);
  }

  function cancelCreds() {
    // fields.awsConfigApiRoleArn = '';
    setIsAddingCredentials(false);
  }

  async function handleSave() {
    try {
      setIsSubmitted(true);
      setIsLoading(true);

      // Validate
      if (!fields.secretId) {
        if (!fields.awsConfigApiRoleArn) {
          console.log('misisng secret');
          setValidated(false);
          setActiveTab('2');
          setIsLoading(false);
          return;
        }
      }
      if (!aggregator) {
        console.log('missing aggregator selection');
        setValidated(false);
        setActiveTab('2');
        setIsLoading(false);
        toast.error('Please select an aggregator');
        return;
      }

      if (!bugsDateBack.length || !snCompanySysId) {
        console.log('failed validation');
        setValidated(false);
        setActiveTab('3');
        setIsLoading(false);
        return;
      }
      if (!priorityMaps.length) {
        console.log('failed validation');
        setValidated(false);
        setActiveTab('4');
        setIsLoading(false);
        return;
      }

      let serviceSecret;

      if (fields.awsConfigApiRoleArn) {
        // Updated secrets
        serviceSecret = await saveAWSArn();
      } else {
        // use existing secrets
        serviceSecret = { Name: fields.secretId };
      }

      if (!serviceSecret || !serviceSecret.Name) {
        setIsLoading(false);
        setValidated(false);
        setActiveTab('2');
        throw Error('Missing Secret');
      }

      await saveConfig(serviceSecret);
      fields.secretId = serviceSecret.Name;
      // set updated secretId
      setBugServiceFrequency();
      setIsLoading(false);
      setIsSubmitted(false);
      setModalOpen(false);
      setIsForceRun(false);
      setIsUpdatingCreds(false);
      setActiveTab('1');
      toast.success(`Settings Saved!`);
    } catch (e) {
      setIsLoading(false);
      toast.error('Failed to save.');
      console.log('e', e);
    }
  }

  function handleValidationError() {
    setModalOpen(!modalOpen);
    animateScroll.scrollTo(70);
    // window.scrollTo(0, 0);
  }

  // Function to handle single selection for aggregator
  function handleAggregatorSelect(selectedOption) {
    setAggregator(selectedOption);
  }
  // Initialize aggregator from settings when component loads
  useEffect(() => {
    if (settings?.aggregatorName) {
      const selectedAggregator = { label: settings.aggregatorName, value: settings.aggregatorName };
      setAggregator(selectedAggregator);
    }
  }, [settings?.aggregatorName]);

  useEffect(() => {
    setIsUpdated(true);

    const isValidPriorityMaps = () => {
      return priorityMaps?.length && !priorityMaps.some(mapping => !mapping.snPriority || !mapping.vendorPriority);
    };

    const hasValidCredentials = () => fields.secretId || fields.awsConfigApiRoleArn;

    if (
      bugsDateBack?.length &&
      snCompanySysId?.value &&
      isValidPriorityMaps() &&
      vendorImpactedRegions.length &&
      aggregator &&
      hasValidCredentials()
    ) {
      setAllRequired(true);
    } else {
      setAllRequired(false);
    }
  }, [
    fields,
    bugsDateBack,
    priorityStats,
    vendorStatuses,
    vendorEventScopes,
    priorityMaps,
    keywords,
    searchFields,
    snCompanySysId,
    aggregator, // Add aggregator to dependencies
    vendorImpactedRegions,
    setAllRequired,
    setIsUpdated
  ]);

  return (
    <>
      <TabNav
        vendorId={vendorId}
        credentials={true}
        activeTab={activeTab}
        snIntegrationCheck={snIntegrationCheck}
        setActiveTab={setActiveTab}
        setIsLoading={setIsLoading}
        isLoading={isLoading}
        setServices={setServices}
        setVendors={setVendors}
        buildVendorStat={buildVendorStat}
        setPriorityStats={setPriorityStats}
        setBugs={setBugs}
        isConfigured={isConfigured}
        loading={loading}
      />

      <TabContent>
        <TabOne
          vendorId={vendorId}
          vendorLabel={vendorLabel}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          snIntegrationCheck={snIntegrationCheck}
          services={services}
          vendors={vendors}
          priorityStats={priorityStats}
          isConfigured={isConfigured}
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          setIsForceRun={setIsForceRun}
          timeUnit={timeUnit}
          setTimeUnit={setTimeUnit}
          timeValue={timeValue}
          setTimeValue={setTimeValue}
          schedulingMode={schedulingMode}
          setSchedulingMode={setSchedulingMode}
          schedules={schedules}
          setSchedules={setSchedules}
          nextRun={nextRun}
          setNextRun={setNextRun}
          loading={loading}
          isServiceActive={isServiceActive}
          setIsServiceActive={setIsServiceActive}
        />
        <form noValidate style={{ minWidth: '100%' }}>
          <TabPane tabId="2">
            {activeTab === '2' && (
              <Card bg={''} text={'dark'} className="mb-4">
                <CardBody>
                  <Row form>
                    <Col lg="12">
                      <FormGroup>
                        <Label>
                          AWS Config Role ARN
                          <span style={{ color: '#bd472a' }}>*</span>
                        </Label>
                        {!isAddingCredentials ? (
                          <>
                            <div>
                              <ButtonIcon
                                onClick={() => setIsAddingCredentials(true)}
                                color="falcon-default"
                                size="sm"
                                icon="plus"
                                iconClassName="fs--2"
                              >
                                Set Credentials
                              </ButtonIcon>

                              {isValidCredentials || fields.secretId ? (
                                <>
                                  {'  '}
                                  <FontAwesomeIcon icon={'check'} style={{ color: 'green', marginLeft: '10px' }} /> AWS
                                  Config Role ARN Stored
                                  {/* Display current aggregator name if saved */}
                                  {!isAddingCredentials && (
                                    <Row form className="mt-3">
                                      <Col lg="6">
                                        <FormGroup>
                                          <Label>Selected Aggregator</Label>
                                          <div
                                            className="form-control-plaintext border rounded p-2 bg-light"
                                            style={{ backgroundColor: 'var(--bg-soft)' }}
                                          >
                                            {settings?.aggregatorName || 'No aggregator selected'}
                                          </div>
                                        </FormGroup>
                                      </Col>
                                    </Row>
                                  )}
                                  {aggregatorOptions?.length > 0 && (
                                    <Row form className="mt-3">
                                      <Col lg="6">
                                        <FormGroup>
                                          <Label>
                                            AWS Config Aggregator
                                            <span style={{ color: '#bd472a' }}>*</span>
                                          </Label>
                                          <div data-testid="aggregator-select">
                                            <Select
                                              value={aggregator}
                                              onChange={handleAggregatorSelect}
                                              options={aggregatorOptions}
                                              isSearchable
                                              placeholder="Select an aggregator"
                                              className={isSubmitted && !aggregator ? 'is-invalid' : ''}
                                            />
                                          </div>
                                          {isSubmitted && !aggregator && (
                                            <FormFeedback className="d-block">
                                              <Alert color="danger">Please select an aggregator</Alert>
                                            </FormFeedback>
                                          )}
                                        </FormGroup>
                                      </Col>
                                    </Row>
                                  )}
                                </>
                              ) : null}
                            </div>
                          </>
                        ) : (
                          <>
                            <Row>
                              <Col lg="6">
                                <FieldGroup
                                  type="text"
                                  id="awsConfigApiRoleArn"
                                  onChange={handleFieldChange}
                                  value={fields.awsConfigApiRoleArn}
                                  showFeedback={!isValidCredentials}
                                  feedback={'AWS Config Role ARN has not been validated or is invalid.'}
                                  placeholder="Enter AWS Config Role ARN"
                                  style={{ marginBottom: '10px' }}
                                  required
                                />
                              </Col>
                            </Row>
                            <CredsModal
                              isTestingValidCredentials={isTestingValidCredentials}
                              setIsTestingCredentials={setIsTestingValidCredentials}
                              fields={fields}
                              testCreds={testCreds}
                              vendorId={vendorId}
                            />
                            <Button
                              onClick={cancelCreds}
                              color="falcon-danger"
                              size="md"
                              iconclassname="fs--2"
                              style={{ marginRight: '10px' }}
                            >
                              Cancel
                            </Button>
                          </>
                        )}
                        <FormFeedback
                          className={!fields.secretId && !fields.awsConfigApiRoleArn && isSubmitted ? 'd-block' : ''}
                          type="invalid"
                        >
                          <Alert color="danger">Please specify credentials.</Alert>
                        </FormFeedback>
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            )}
          </TabPane>

          <DefaultConfigTab
            vendor={vendor}
            activeTab={activeTab}
            vendorLabel={vendorLabel}
            bugsDateBack={bugsDateBack}
            setBugsDateBack={setBugsDateBack}
            isSubmitted={isSubmitted}
            snCompanySysIdErrors={snCompanySysIdErrors}
            snCompanySysIdOptions={snCompanySysIdOptions}
            setSnCompanySysId={setSnCompanySysId}
            snCompanySysId={snCompanySysId}
            fields={fields}
            handleFieldChange={handleFieldChange}
          />

          <TabPane tabId="4">
            {activeTab === '4' && (
              <>
                <ProductPriority
                  title="Default Bug Configuration"
                  vendorPriorityOptions={vendorPriorityOptions}
                  vendorStatusOptions={vendorStatusOptions}
                  vendorResolutionOptions={vendorResolutionOptions}
                  setVendorStatuses={setVendorStatuses}
                  vendorStatuses={vendorStatuses}
                  isSubmitted={isSubmitted}
                  priorityMaps={priorityMaps}
                  setVendorResolutions={setVendorResolutions}
                  vendorResolutions={vendorResolutions}
                  setPriorityMaps={setPriorityMaps}
                  snPriorityOptions={snPriorityOptions}
                  keywords={keywords}
                  setVendorBugTypes={setVendorBugTypes}
                  vendorBugTypes={vendorBugTypes}
                  vendorBugTypeOptions={vendorBugTypeOptions}
                  setKeywords={setKeywords}
                  searchFields={searchFields}
                  vendorBugCategories={vendorBugCategories}
                  setVendorBugCategories={setVendorBugCategories}
                  setSearchFields={setSearchFields}
                  requireImpactedRegions={false}
                  vendorImpactedRegions={vendorImpactedRegions}
                  setVendorImpactedRegions={setVendorImpactedRegions}
                  vendorImpactedRegionOptions={vendorImpactedRegionOptions}
                />
              </>
            )}
          </TabPane>
          <TabPane tabId="6">{activeTab === '6' && <ExecutionStatsTab vendorId={vendorId} />}</TabPane>
          <Row className={`${activeTab === '1' ? 'd-none' : ''} flex-between-center`}>
            <Col xs="auto">
              <Button
                block
                color="falcon-danger"
                onClick={() => {
                  console.log(`/vendors/${vendorId}/configuration`);
                  navigate(-1);
                }}
              >
                Cancel
              </Button>
            </Col>
            <Col xs="auto">
              <SummaryModal
                modalOpen={modalOpen}
                isReadOnly={isReadOnly}
                setModalOpen={setModalOpen}
                handleSave={handleSave}
                isLoading={isLoading}
                vendorLabel={vendorLabel?.replace('Systems', 'Networks')}
                vendorId={vendorId}
                validated={validated}
                setValidated={setValidated}
                syncOnly={syncOnly}
                setSyncOnly={setSyncOnly}
                handleValidationError={handleValidationError}
                allRequired={allRequired}
                isUpdated={isUpdated}
              />
            </Col>
          </Row>
        </form>
      </TabContent>
    </>
  );
};
ConfigFormAwsConfig.propTypes = {
  vendor: PropTypes.object,
  isReadOnly: PropTypes.bool,
  settings: PropTypes.object,
  vendorLabel: PropTypes.string,
  vendorId: PropTypes.string,
  vendorPriorityOptions: PropTypes.array,
  vendorStatusOptions: PropTypes.array,
  vendorResolutionOptions: PropTypes.array,
  vendorEventScopeOptions: PropTypes.array,
  snCompanySysIdOptions: PropTypes.object,
  snCompanySysId: PropTypes.object,
  snIntegrationCheck: PropTypes.bool,
  services: PropTypes.array,
  vendors: PropTypes.array,
  bugs: PropTypes.object,
  priorityStats: PropTypes.object,
  setPriorityStats: PropTypes.func,
  bugOptions: PropTypes.array,
  selectedBugOption: PropTypes.object,
  setSelectedBugOption: PropTypes.func,
  snCompanySysIdErrors: PropTypes.bool,
  vendorEventScopes: PropTypes.array,
  setVendorEventScopes: PropTypes.func,
  vendorResolutions: PropTypes.array,
  setVendorResolutions: PropTypes.func,
  setVendorStatuses: PropTypes.func,
  vendorStatuses: PropTypes.array,
  fields: PropTypes.object,
  handleFieldChange: PropTypes.func,
  priorityMaps: PropTypes.array,
  setPriorityMaps: PropTypes.func,
  snPriorityOptions: PropTypes.array,
  keywords: PropTypes.array,
  setKeywords: PropTypes.func,
  searchFields: PropTypes.array,
  setSearchFields: PropTypes.func,
  setSnCompanySysId: PropTypes.func,
  bugsDateBack: PropTypes.string,
  setBugsDateBack: PropTypes.func,
  setServices: PropTypes.func,
  setVendors: PropTypes.func,
  buildVendorStat: PropTypes.func,
  setBugs: PropTypes.func,
  isConfigured: PropTypes.bool,
  syncOnly: PropTypes.bool,
  setSyncOnly: PropTypes.func,
  loading: PropTypes.bool,
  setIsServiceActive: PropTypes.func,
  vendorBugTypes: PropTypes.array,
  setVendorBugTypes: PropTypes.func,
  isServiceActive: PropTypes.number,
  organizations: PropTypes.array,
  vendorBugCategories: PropTypes.array,
  vendorBugCategoryOptions: PropTypes.array,
  vendorBugTypeOptions: PropTypes.array,
  vendorImpactedRegions: PropTypes.array,
  setVendorImpactedRegions: PropTypes.func,
  vendorImpactedRegionOptions: PropTypes.array,
  setVendorBugCategories: PropTypes.func,
  setOrganizations: PropTypes.func,
  organizationOptions: PropTypes.array,
  setOrganizationOptions: PropTypes.func,
  awsOrgHealthEnabled: PropTypes.bool,
  setAwsOrgHealthEnabled: PropTypes.func,
  awsOrgHealthSupport: PropTypes.bool,
  allRequired: PropTypes.bool,
  isUpdated: PropTypes.bool,
  setAllRequired: PropTypes.func,
  setIsUpdated: PropTypes.func
};
export default ConfigFormAwsConfig;
